import { useRef, useEffect, useCallback, useState } from 'react';
import {
  useFloating,
  arrow,
  flip,
  shift,
  FloatingArrow,
  autoUpdate,
  useClick,
  useDismiss,
  useInteractions,
} from '@floating-ui/react';
import { XMarkIcon } from '@heroicons/react/16/solid';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { Objective1 } from '../../types';
import { AddEditDeleteBar1 } from './AddEditDeleteBar1';
import { useSearchParams } from 'react-router-dom';

interface ObjectiveTooltip2Props {
  cellId: string;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  isInUse: boolean;
  setIsInUse: (isInUse: boolean) => void;
  onClose: () => void;
  referenceElement: HTMLElement | null;
  objectiveUuid: string;
  milestoneMapUuid: string;
  onEdit: (newText: string) => Promise<void>;
  onAdd: () => Promise<void>;
  onDelete: () => Promise<void>;
  onRemove: () => Promise<void>;
  onInfo: () => void;
  showAdd: boolean;
  showEdit: boolean;
  showDelete: boolean;
  showRemove: boolean;
  startInEditMode?: boolean;
}

export function ObjectiveTooltip2({
  cellId,
  isOpen,
  setIsOpen,
  isInUse,
  setIsInUse,
  onClose,
  referenceElement,
  objectiveUuid,
  milestoneMapUuid,
  onEdit,
  onAdd,
  onDelete,
  onRemove,
  onInfo,
  showAdd,
  showEdit,
  showDelete,
  showRemove,
  startInEditMode = false,
}: ObjectiveTooltip2Props) {
  const arrowRef = useRef(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState('');
  const [isActionBarVisible, setIsActionBarVisible] = useState(false);
  const [, setSearchParams] = useSearchParams();

  const objective = useSelector((state: RootState) =>
    state.map3Data.objectives.find((obj: Objective1) => obj.uuid === objectiveUuid),
  );

  // Start in edit mode if requested
  useEffect(() => {
    if (startInEditMode && isOpen && objective) {
      setIsEditing(true);
      setEditText(objective.text || '');
    }
  }, [startInEditMode, isOpen, objective]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setIsInUse(false);
    setIsEditing(false);
    setIsActionBarVisible(false);
    // Return focus to the objective cell
    if (referenceElement) {
      referenceElement.focus();
    }
    onClose();
  }, [onClose, setIsOpen, setIsInUse, referenceElement]);

  const { refs, floatingStyles, context } = useFloating({
    whileElementsMounted: autoUpdate,
    placement: 'top',
    open: isOpen,
    onOpenChange: (isOpen: boolean) => {
      if (!isOpen) {
        handleClose();
      }
    },
    elements: {
      reference: referenceElement,
    },
    middleware: [
      flip(),
      shift(),
      arrow({
        element: arrowRef,
      }),
    ],
  });

  const setRefs = useCallback(
    (node: HTMLDivElement | null) => {
      tooltipRef.current = node;
      refs.setFloating(node);
    },
    [refs],
  );

  useEffect(() => {
    if (isOpen && tooltipRef.current) {
      tooltipRef.current.focus();
    }
  }, [isOpen]);

  // Focus trapping for the tooltip
  const handleTooltipKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Tab') {
      const focusableElements = tooltipRef.current?.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );

      if (focusableElements && focusableElements.length > 0) {
        const firstElement = focusableElements[0] as HTMLElement;
        const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

        if (e.shiftKey) {
          // Shift+Tab: if on first element, wrap to last
          if (document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          }
        } else {
          // Tab: if on last element, wrap to first
          if (document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      }
    }
  }, []);

  const dismiss = useDismiss(context, {
    escapeKey: true,
    outsidePress: true,
    referencePress: false,
    bubbles: false,
  });
  const click = useClick(context);
  const { getFloatingProps } = useInteractions([click, dismiss]);

  // Open drawer in edit mode via setSearchParams (functional merge preserves other params)
  const handleEdit = () => {
    if (objective?.shortId) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('drawer', 'objective');
        next.set('objectiveId', objective.shortId);
        next.set('tab', 'info');
        next.set('edit', 'title');
        return next;
      });
    }
  };

  const handleSave = async () => {
    if (!objective) return;

    const trimmedText = editText.trim();
    if (!trimmedText) {
      console.warn('Cannot save objective with empty text');
      return;
    }

    if (trimmedText !== objective.text) {
      await onEdit(trimmedText);
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditText(objective?.text || '');
  };

  const handleInfoClick = () => {
    if (objective?.shortId) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('drawer', 'objective');
        next.set('objectiveId', objective.shortId);
        next.set('tab', 'info');
        return next;
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div
      id={`floating-${cellId}`}
      ref={setRefs}
      style={{
        ...floatingStyles,
        position: 'fixed',
      }}
      {...getFloatingProps({
        onKeyDown: (e) => {
          handleTooltipKeyDown(e);
          if (e.key !== 'Escape') {
            e.stopPropagation();
          }
        },
      })}
      className="bg-slate-950 p-1 rounded-xl shadow-lg z-[9999] max-h-[90vh] max-w-[90vw] flex flex-col"
      onFocus={() => setIsInUse(true)}
      tabIndex={-1}
    >
      <div className="overflow-hidden rounded-lg bg-white text-black-500 dark:border-white/10 dark:bg-zinc-800 dark:text-zinc-300 drop-shadow-2xl z-50 max-w-lg min-w-60 w-full flex flex-col max-h-full">
        <div className="overflow-y-auto flex-1 min-h-0">
          <TooltipContent
            objective={objective}
            isEditing={isEditing}
            editText={editText}
            setEditText={setEditText}
            onSave={handleSave}
            onCancel={handleCancel}
            isActionBarVisible={isActionBarVisible}
            setIsActionBarVisible={setIsActionBarVisible}
            onEdit={handleEdit}
            onAdd={onAdd}
            onDelete={onDelete}
            onRemove={onRemove}
            onInfo={handleInfoClick}
            onClose={handleClose}
            showAdd={showAdd}
            showEdit={showEdit}
            showDelete={showDelete}
            showRemove={showRemove}
          />
        </div>
      </div>
      <FloatingArrow ref={arrowRef} context={context} />
    </div>
  );
}

function TooltipContent({
  objective,
  isEditing,
  editText,
  setEditText,
  onSave,
  onCancel,
  isActionBarVisible,
  setIsActionBarVisible,
  onEdit,
  onAdd,
  onDelete,
  onRemove,
  onInfo,
  onClose,
  showAdd,
  showEdit,
  showDelete,
  showRemove,
}: {
  objective: Objective1 | undefined;
  isEditing: boolean;
  editText: string;
  setEditText: (text: string) => void;
  onSave: () => Promise<void>;
  onCancel: () => void;
  isActionBarVisible: boolean;
  setIsActionBarVisible: (visible: boolean) => void;
  onEdit: () => void;
  onAdd: () => Promise<void>;
  onDelete: () => Promise<void>;
  onRemove: () => Promise<void>;
  onInfo: () => void;
  onClose: () => void;
  showAdd: boolean;
  showEdit: boolean;
  showDelete: boolean;
  showRemove: boolean;
}) {
  const isLoading = useSelector((state: RootState) => state.map3Data.isLoading);
  const [actionBarActive, setActionBarActive] = useState(false);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isEditing) {
      e.preventDefault();
      setActionBarActive(true);
    }
  };

  const handleActionBarEscape = () => {
    setActionBarActive(false);
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!objective?.uuid) {
    console.error(`Objective not found: ${objective}`);
    return <div>Error loading objective</div>;
  }

  return (
    <div className="px-2 py-2">
      <div
        tabIndex={0}
        className="relative p-1 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/20 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        onMouseEnter={() => setIsActionBarVisible(true)}
        onMouseLeave={() => setIsActionBarVisible(false)}
        onFocus={() => setIsActionBarVisible(true)}
        onBlur={() => setIsActionBarVisible(false)}
        onKeyDown={handleKeyDown}
      >
        {isEditing ? (
          <div className="space-y-2">
            <textarea
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              className="w-full px-2 py-1 rounded border border-gray-300 dark:bg-zinc-700 dark:text-white min-h-[100px] resize-vertical"
              autoFocus
              placeholder="Enter objective text..."
            />
            <div className="flex justify-end space-x-2">
              <button
                onClick={onCancel}
                className="px-3 py-1 text-sm bg-gray-200 hover:bg-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                Cancel
              </button>
              <button
                onClick={onSave}
                className="px-3 py-1 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                Save
              </button>
            </div>
          </div>
        ) : (
          <div className="relative flex justify-between items-start">
            <div className="flex-grow whitespace-pre-wrap pr-2">
              {objective.text || 'No objective text'}
            </div>
            <div className="flex-shrink-0 pl-2">
              <button
                onClick={onClose}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    onClose();
                  }
                }}
                className="h-5 w-5 text-black dark:text-white hover:bg-blue-600 hover:text-white cursor-pointer rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                aria-label="Close tooltip"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="absolute top-1/2 right-6 -translate-y-1/2">
              <AddEditDeleteBar1
                isVisible={isActionBarVisible}
                showAdd={showAdd}
                showEdit={showEdit}
                showDelete={showDelete}
                showRemove={showRemove}
                showInfo={true}
                onEdit={onEdit}
                onAdd={onAdd}
                onDelete={onDelete}
                onRemove={onRemove}
                onInfo={onInfo}
                actionBarActive={actionBarActive}
                onActionBarEscape={handleActionBarEscape}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
